import { Info } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { InputFieldWithCurrency, SingleSelect, SingleSelectOption } from '@/modules/common';
import { DEFAULT_CURRENCY_CODE } from '@/modules/common/constants/currency.constants';
import { ExternalRatesFormValues } from '@/modules/settings/validations/external-rates-schema';

interface RateConfigurationCardProps {
  form: UseFormReturn<ExternalRatesFormValues>;
  isLoading: boolean;
  hasData: boolean;
  currencyOptions: SingleSelectOption[];
  rateSourceOptions: SingleSelectOption[];
}

export const RateConfigurationCard = ({
  form,
  isLoading,
  hasData,
  currencyOptions,
  rateSourceOptions,
}: RateConfigurationCardProps) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);

  const selectedCurrencyCode = form.watch('defaultCurrencyCode');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('authenticated.settings:pages.finance.card.title')}</CardTitle>
        <CardDescription>
          {t('authenticated.settings:pages.finance.card.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6">
            {!isLoading && hasData && (
              <>
                {/* Currency Selector */}
                <SingleSelect
                  name="defaultCurrencyCode"
                  control={form.control}
                  label={t('authenticated.settings:pages.finance.fields.currency.label')}
                  options={currencyOptions}
                  placeholder={t(
                    'authenticated.settings:pages.finance.fields.currency.placeholder'
                  )}
                  searchable={true}
                  isClearSelectionEnabled={false}
                />

                {/* Rate Source Selector */}
                <SingleSelect
                  name="defaultProjectRateSource"
                  control={form.control}
                  label={t('authenticated.settings:pages.finance.fields.rateSource.label')}
                  options={rateSourceOptions}
                  placeholder={t(
                    'authenticated.settings:pages.finance.fields.rateSource.placeholder'
                  )}
                  searchable={false}
                  isClearSelectionEnabled={false}
                />

                {/* Organization Default Rate */}
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 hover:bg-transparent"
                        >
                          <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent className="max-w-xs">
                          <p>
                            {t('authenticated.settings:pages.finance.fields.defaultRate.tooltip')}
                          </p>
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t('authenticated.settings:pages.finance.fields.defaultRate.label')}
                    </label>
                  </div>
                  <InputFieldWithCurrency
                    name="defaultRate"
                    control={form.control}
                    placeholder={t(
                      'authenticated.settings:pages.finance.fields.defaultRate.placeholder'
                    )}
                    suffixText={t('common:rateUnits.perHour', {
                      currency: selectedCurrencyCode || DEFAULT_CURRENCY_CODE,
                    })}
                    maxDecimalPlaces={0}
                    isReadMode={false}
                  />
                </div>
              </>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
